import {
    centsToFormattedDollars,
    formatCurrencyInputOnChange,
    formatDecimalInput,
    formattedDollarsToCents,
} from '@fruitful/fruitils';
import {
    type AccountCategory,
    HolisticCategory,
    type HolisticSummary,
    type HolisticSummaryCategory,
    Provider,
} from '@fruitful/types';
import { useQueryClient } from '@tanstack/react-query';
import { Redirect, router } from 'expo-router';
import { useAtom } from 'jotai';
import { capitalize, cloneDeep } from 'lodash';
import React, { type ReactNode, useMemo, useRef, useState } from 'react';
import { KeyboardAvoidingView, Platform, type TextInput } from 'react-native';
import {
    BodyMediumRegular,
    Button,
    ButtonVariant,
    FullScreenSpinner,
    Icon,
    isWeb,
    LabelRegular,
    SafeAreaView,
    ScrollView,
    SelectDropdown,
    Separator,
    StyledBodyMediumRegularInputNoOutline,
    TextArea,
    tokens,
    useToastController,
    View,
    XIcon,
    XStack,
    YStack,
} from 'smoothie';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import useCreateManualAccount from '../../holistic/hooks/useCreateManualAccount';
import useUpdateManualAccount from '../../holistic/hooks/useUpdateManualAccount';
import { holisticSummaryAtom } from '../../holistic/state/HolisticAtoms';
import type { IManualAccount } from '../../holistic/types/ManualAccount';
import { captureException } from '../../logger/Logger';
import {
    selectedManualAccountAtom,
    selectedManualInstitutionAtom,
} from '../state/OnboardingAtoms';

const INPUT_STYLE_PROPS = {
    backgroundColor: tokens.color.light.gray.gray200,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: tokens.color.light.gray.gray600,
    borderRadius: 16,
};

const EditInstitutionAccountField = ({
    label,
    input,
}: {
    label: string;
    input: ReactNode;
}) => {
    return (
        <YStack gap={8}>
            <LabelRegular color="$gray1000">{label}</LabelRegular>
            {input}
        </YStack>
    );
};

const EditManualAccount = () => {
    const toast = useToastController();
    const user = useAuthedUser();

    const [selectedManualInstitution, setSelectedManualInstitution] = useAtom(
        selectedManualInstitutionAtom,
    );
    const [selectedManualAccount, setSelectedManualAccount] = useAtom(
        selectedManualAccountAtom,
    );
    const [isSubmitting, setIsSubmitting] = useState(false);

    const institutionName = useMemo(
        () =>
            selectedManualAccount?.institutionName ?? selectedManualInstitution,
        [selectedManualAccount, selectedManualInstitution],
    );

    const [name, setName] = useState<string>(selectedManualAccount?.name ?? '');

    const [holisticCategory, setHolisticCategory] = useState<string>(
        capitalize(selectedManualAccount?.category) ??
            capitalize(HolisticCategory.SPEND),
    );

    const [balance, setBalance] = useState<string | null>(
        selectedManualAccount?.currentBalanceCents == null
            ? ''
            : centsToFormattedDollars(
                  selectedManualAccount?.currentBalanceCents ?? 0,
              ),
    );

    const [minimumPayment, setMinimumPayment] = useState<string | null>(
        selectedManualAccount?.minimumPayment == null
            ? ''
            : centsToFormattedDollars(
                  selectedManualAccount?.minimumPayment ?? 0,
              ),
    );

    const [interestRate, setInterestRate] = useState<string | undefined>(
        formatDecimalInput(
            selectedManualAccount?.interestRate?.toString() ?? '0',
        ),
    );

    const [description, setDescription] = useState<string | undefined>(
        selectedManualAccount?.description ?? '',
    );

    const [holisticSummary, setHolisticSummary] = useAtom(holisticSummaryAtom);

    const { mutate: updateManualAccount, isPending: isUpdating } =
        useUpdateManualAccount();
    const { mutate: createManualAccount, isPending: isCreating } =
        useCreateManualAccount();
    const queryClient = useQueryClient();

    const isValid = useMemo(() => {
        return name.trim() !== '' && holisticCategory != null && balance !== '';
    }, [
        name,
        holisticCategory,
        balance,
        minimumPayment,
        interestRate,
        description,
    ]);

    const onSuccess = async (updatedAccount: Partial<IManualAccount>) => {
        await queryClient.invalidateQueries({
            queryKey: [`getAccountOverviewSummary/${user.id}`],
        });
        if (holisticSummary) {
            if (selectedManualAccount) {
                const newSummary = Object.fromEntries(
                    Object.entries(holisticSummary).map(([category, group]) => [
                        category,
                        {
                            ...group,
                            accounts: group.accounts.map((account) =>
                                account.id === updatedAccount.id
                                    ? {
                                          ...updatedAccount,
                                          provider: Provider.MANUAL,
                                      }
                                    : account,
                            ),
                        },
                    ]),
                ) as HolisticSummary;
                setHolisticSummary(cloneDeep(newSummary));
            } else {
                const holisticSummaryCategory =
                    holisticCategory as HolisticSummaryCategory;

                setHolisticSummary({
                    ...holisticSummary,
                    [holisticSummaryCategory]: {
                        accounts: [
                            ...holisticSummary[holisticSummaryCategory]
                                .accounts,
                            { ...updatedAccount, provider: Provider.MANUAL },
                        ],
                    },
                });
            }
        }

        setIsSubmitting(false);
        setSelectedManualAccount(null);
        setSelectedManualInstitution(null);
        router.back();
    };

    const onError = (error: Error) => {
        setIsSubmitting(false);
        toast.show('Could not update manual account');
        captureException(error);
    };

    const onSave = () => {
        setIsSubmitting(true);

        if (selectedManualAccount != null) {
            updateManualAccount(
                {
                    body: {
                        id: selectedManualAccount.id,
                        membershipId: user.membership?.id as string,
                        name,
                        holisticCategory:
                            holisticCategory.toUpperCase() as AccountCategory,
                        amount: formattedDollarsToCents(balance ?? ''),
                        minimumPayment: formattedDollarsToCents(
                            minimumPayment ?? '',
                        ),
                        interestRate:
                            interestRate != null
                                ? parseFloat(interestRate)
                                : null,
                        description,
                    },
                },
                { onSuccess, onError },
            );
        } else {
            createManualAccount(
                {
                    body: {
                        userId: user.id,
                        membershipId: user.membership?.id as string,
                        institutionName: selectedManualInstitution ?? '',
                        name,
                        holisticCategory:
                            holisticCategory.toUpperCase() as AccountCategory,
                        amount: formattedDollarsToCents(balance ?? ''),
                        minimumPayment: formattedDollarsToCents(
                            minimumPayment ?? '',
                        ),
                        interestRate:
                            interestRate != null
                                ? parseFloat(interestRate)
                                : null,
                        description,
                        type: null,
                    },
                },
                {
                    onSuccess,
                    onError,
                },
            );
        }
    };

    const interestRateInput = useRef<TextInput | null>(null);

    const [interestRateWidth, setInterestRateWidth] = useState(0);

    if (selectedManualInstitution == null) {
        return <Redirect href="/" />;
    }

    if (isSubmitting) {
        return <FullScreenSpinner />;
    }

    return (
        <SafeAreaView style={{ flex: 1 }} edges={['left', 'bottom', 'right']}>
            <YStack
                alignItems="center"
                paddingHorizontal={16}
                paddingTop={24}
                flex={1}
            >
                <YStack
                    flex={1}
                    alignItems="center"
                    width="100%"
                    maxWidth={isWeb ? 700 : '100%'}
                >
                    <XStack
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                    >
                        <View
                            cursor="pointer"
                            onPress={() => {
                                router.back();
                            }}
                            padding={6}
                        >
                            <Icon icon={XIcon} size={20} />
                        </View>
                        <BodyMediumRegular>
                            {`${institutionName} - ${selectedManualAccount ? selectedManualAccount.name : 'New account'}`}
                        </BodyMediumRegular>
                        <View width={32} height={32} />
                    </XStack>
                    <KeyboardAvoidingView
                        style={{
                            flex: 1,
                            height: '100%',
                            width: '100%',
                            ...(isWeb && {
                                justifyContent: 'center',
                                alignItems: 'center',
                            }),
                        }}
                        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
                        keyboardVerticalOffset={0}
                    >
                        <ScrollView
                            showsVerticalScrollIndicator={isWeb}
                            flex={1}
                            width="100%"
                            contentContainerStyle={{
                                flexGrow: 1,
                                width: '100%',
                            }}
                            automaticallyAdjustKeyboardInsets={true}
                        >
                            <YStack
                                gap={24}
                                paddingTop={16}
                                width="100%"
                                flex={1}
                            >
                                <EditInstitutionAccountField
                                    label="Account nickname"
                                    input={
                                        <StyledBodyMediumRegularInputNoOutline
                                            value={name}
                                            onChangeText={(v) => {
                                                setName(v);
                                            }}
                                            {...INPUT_STYLE_PROPS}
                                        />
                                    }
                                />
                                <EditInstitutionAccountField
                                    label="Category"
                                    input={
                                        <SelectDropdown
                                            options={[
                                                'Spend',
                                                'Save',
                                                'Invest',
                                                'Borrow',
                                            ]}
                                            defaultValue={holisticCategory}
                                            onSelect={(category) => {
                                                setHolisticCategory(
                                                    category.toLowerCase(),
                                                );
                                            }}
                                            buttonStyle={{
                                                ...INPUT_STYLE_PROPS,
                                            }}
                                            buttonTextStyle={{
                                                fontWeight: '500',
                                            }}
                                            rowItemTextStyle={{
                                                fontWeight: '500',
                                            }}
                                        />
                                    }
                                />
                                <Separator />
                                <EditInstitutionAccountField
                                    label="Balance"
                                    input={
                                        <StyledBodyMediumRegularInputNoOutline
                                            {...INPUT_STYLE_PROPS}
                                            keyboardType="decimal-pad"
                                            value={balance ?? '0'}
                                            onChangeText={(v) => {
                                                const value =
                                                    v === '$ '
                                                        ? ''
                                                        : formatCurrencyInputOnChange(
                                                              balance ?? '',
                                                              v,
                                                          );

                                                setBalance(value);
                                            }}
                                        />
                                    }
                                />
                                <EditInstitutionAccountField
                                    label="Minimum payment (optional)"
                                    input={
                                        <StyledBodyMediumRegularInputNoOutline
                                            {...INPUT_STYLE_PROPS}
                                            keyboardType="decimal-pad"
                                            value={minimumPayment ?? '0'}
                                            onChangeText={(v) => {
                                                const value =
                                                    v === '$ '
                                                        ? ''
                                                        : formatCurrencyInputOnChange(
                                                              minimumPayment ??
                                                                  '',
                                                              v,
                                                          );

                                                setMinimumPayment(value);
                                            }}
                                        />
                                    }
                                />
                                <EditInstitutionAccountField
                                    label="Interest rate (optional)"
                                    input={
                                        <XStack
                                            alignItems="center"
                                            justifyContent="flex-start"
                                            {...INPUT_STYLE_PROPS}
                                            onPress={() =>
                                                interestRateInput.current?.focus()
                                            }
                                        >
                                            {/* hidden element to help determine input width so it only stretches as far as its content */}
                                            <View
                                                position="absolute"
                                                opacity={0}
                                                onLayout={(e) => {
                                                    setInterestRateWidth(
                                                        e.nativeEvent.layout
                                                            .width,
                                                    );
                                                }}
                                            >
                                                <BodyMediumRegular>
                                                    {interestRate}
                                                </BodyMediumRegular>
                                            </View>
                                            <StyledBodyMediumRegularInputNoOutline
                                                ref={interestRateInput}
                                                width={interestRateWidth}
                                                keyboardType="decimal-pad"
                                                value={(
                                                    interestRate ?? 0
                                                ).toString()}
                                                onChangeText={(v) => {
                                                    setInterestRate(
                                                        formatDecimalInput(v),
                                                    );
                                                }}
                                            />
                                            <BodyMediumRegular>
                                                %
                                            </BodyMediumRegular>
                                        </XStack>
                                    }
                                />
                                <EditInstitutionAccountField
                                    label="Description (optional)"
                                    input={
                                        <TextArea
                                            value={description}
                                            onChangeText={(v) => {
                                                setDescription(v);
                                            }}
                                            numberOfLines={5}
                                            multiline={true}
                                            scrollEnabled={false}
                                            minHeight={132}
                                            fontWeight="500"
                                            paddingBottom={16}
                                            focusVisibleStyle={{
                                                outlineWidth: 0,
                                                borderWidth: 0,
                                            }}
                                            {...INPUT_STYLE_PROPS}
                                        />
                                    }
                                />
                            </YStack>
                            <View paddingTop={16}>
                                <Button
                                    variant={ButtonVariant.PRIMARY}
                                    text="Save"
                                    onPress={onSave}
                                    disabled={
                                        !isValid ||
                                        isCreating ||
                                        isUpdating ||
                                        isSubmitting
                                    }
                                />
                            </View>
                        </ScrollView>
                    </KeyboardAvoidingView>
                </YStack>
            </YStack>
        </SafeAreaView>
    );
};

export default EditManualAccount;
